// Route guard for INTERNAL surfaces (currently the embedded Flow studio).
//
// Hiding a nav item is presentation, not access control: the URL still resolves,
// and /flow edits the endpoints and services every project runs on. This gate is
// what actually keeps a non-staff user out of the engine editor.
//
// It is a CLIENT-SIDE gate over a surface whose real authority is the Flow API —
// so treat it as "don't show customers our internals", not as a security boundary.
// A determined user with a valid token still has whatever the Flow API grants
// them; enforcing that properly belongs on the Flow side.
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { createPageUrl } from "@/utils";
import { useAuth } from "@/contexts/AuthContext";
import { isMindshineStaff } from "@/utils/staff";

export default function StaffOnly({ children }) {
  const { user, isLoading } = useAuth();

  // Wait for the session before judging: rendering the denial during load would
  // flash "not available" at staff on every hard refresh.
  if (isLoading) return null;

  if (!isMindshineStaff(user)) {
    return (
      <div className="min-h-full flex flex-col items-center justify-center gap-3 px-6 py-16 text-center bg-page-bg">
        <Lock size={32} className="text-page-muted" />
        <div className="text-lg font-medium text-page-title">This area isn't available</div>
        <p className="text-sm text-page-muted max-w-[460px]">
          Flow is an internal engineering tool for the cortex team. Everything it
          builds shows up in your project's own pages.
        </p>
        <Link
          to={createPageUrl("dashboard")}
          className="mt-2 h-9 px-4 inline-flex items-center rounded-[5px] bg-nav-active text-white text-sm font-medium no-underline hover:bg-nav-active-dark transition-colors"
        >
          Back to dashboard
        </Link>
      </div>
    );
  }

  return children;
}
